import type { ColorToken } from '@backrs_ui/common-ui';

export type ProgramCategory = Readonly<{
  id: string;
  label: string;
  /** Compact label for narrow layouts; falls back to `label`. */
  legendLabel?: string;
  color: ColorToken;
}>;

export const OTHER_CATEGORY: ProgramCategory = {
  id: 'other',
  label: 'Other',
  color: 'Gray/300',
};

/** Declaration order is the legend order. */
export const PROGRAM_CATEGORIES: readonly ProgramCategory[] = [
  { id: 'sciences', label: 'Sciences', color: 'Brand/800' },
  { id: 'engineering', label: 'Engineering', color: 'Brand/500' },
  { id: 'computer-science', label: 'Computer Science', color: 'Brand/300' },
  { id: 'business', label: 'Business', color: 'Indigo/600' },
  { id: 'communication', label: 'Communication', color: 'Indigo/300' },
  { id: 'health', label: 'Health', color: 'Teal/600' },
  { id: 'kinesiology', label: 'Kinesiology & Fitness', color: 'Teal/300' },
  { id: 'social-sciences', label: 'Social Sciences', color: 'Purple/600' },
  { id: 'humanities', label: 'Humanities', color: 'Purple/400' },
  {
    id: 'interdisciplinary',
    label: 'Multi/Interdisciplinary Studies',
    legendLabel: 'Multi/Interdisciplinary',
    color: 'Purple/200',
  },
  { id: 'visual-performing-arts', label: 'Visual & Performing Arts', color: 'Pink/500' },
  { id: 'architecture', label: 'Architecture', color: 'Pink/300' },
  { id: 'education', label: 'Education', color: 'Yellow/400' },
  { id: 'criminal-justice-legal', label: 'Criminal Justice & Legal Studies', color: 'Orange/500' },
  { id: 'agriculture', label: 'Agriculture & Natural Resources', color: 'Green/500' },
  OTHER_CATEGORY,
];

const CATEGORY_BY_ID: ReadonlyMap<string, ProgramCategory> = new Map(
  PROGRAM_CATEGORIES.map((category) => [category.id, category]),
);

const CATEGORY_ORDER: ReadonlyMap<string, number> = new Map(
  PROGRAM_CATEGORIES.map((category, index) => [category.id, index]),
);

export const categoryOrder = (category: ProgramCategory): number =>
  CATEGORY_ORDER.get(category.id) ?? PROGRAM_CATEGORIES.length;

/** A category the API knows and this build does not is shown as Other rather than dropped. */
export const categoryById = (id: string): ProgramCategory =>
  CATEGORY_BY_ID.get(id) ?? OTHER_CATEGORY;
